import { apiClient } from "./base/http";

export interface CommissionTier {
  id?: string;
  min_revenue: number;
  max_revenue: number | null;
  rate: number;
}

export interface BonusTier {
  id?: string;
  name: string;
  amount: number;
  frequency: 'monthly' | 'yearly';
}

export interface Staff {
  id: string;
  name: string;
  display_name: string | null;
  phone: string | null;
  entry_date: string | null;
  note: string | null;
  avatar: string | null;
  position: string | null;
  salary: number;
  commission: number;
  random_commission: number;
  /** 業績抽成模式：percent = 抽成階層依營收套 %；fixed = 每做一次服務抽 commission_fixed_amount（不分服務） */
  commission_mode?: "percent" | "fixed";
  commission_fixed_amount?: number | string | null;
  bonus: number;
  is_active: boolean;
  deleted_at?: string | null;
  color?: string | null;
  commission_tiers?: CommissionTier[];
  random_commission_tiers?: CommissionTier[];
  bonus_tiers?: BonusTier[];
  shopIds?: string[];
  portalVisibleShopIds?: string[];
  modules?: string[];
  account_user_id?: string | null;
  account_email?: string | null;
  has_account?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateStaffPayload {
  name: string;
  display_name?: string;
  phone: string;
  entry_date: string;
  note?: string | null;
  avatar?: string | null;
  position?: string;
  salary?: number;
  commission?: number;
  random_commission?: number;
  commission_mode?: "percent" | "fixed";
  commission_fixed_amount?: number | null;
  bonus?: number;
  commission_tiers?: CommissionTier[];
  random_commission_tiers?: CommissionTier[];
  bonus_tiers?: BonusTier[];
  shopIds?: string[];
  portalVisibleShopIds?: string[];
  permissions?: string[];
  color?: string;
  is_active?: boolean;
}

export interface UpdateStaffPayload {
  name?: string;
  displayName?: string;
  phone?: string;
  position?: string;
  note?: string | null;
  avatar?: string | null;
  salary?: number;
  commission?: number;
  randomCommission?: number;
  commissionMode?: "percent" | "fixed";
  commissionFixedAmount?: number | null;
  bonus?: number;
  commissionTiers?: CommissionTier[];
  randomCommissionTiers?: CommissionTier[];
  bonusTiers?: BonusTier[];
  entryDate?: string;
  shopIds?: string[];
  portalVisibleShopIds?: string[];
  permissions?: string[];
  color?: string;
  is_active?: boolean;
}

export interface GetStaffsParams {
  search?: string;
  position?: string;
  page?: number;
  limit?: number;
  offset?: number;
}

export interface StaffsResponse {
  success: boolean;
  staffs: Staff[];
  message?: string;
}

export interface StaffResponse {
  success: boolean;
  staff?: Staff;
  message?: string;
}

export interface StaffAccountPayload {
  email: string;
  password?: string;
  shopScope: "all" | "specific";
  shopIds?: string[];
  modules?: string[];
}

export interface StaffAccountResponse {
  success: boolean;
  message: string;
  staff?: Staff;
}

export interface WorkingHour {
  id?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  shopId?: string | null;
}

export interface SpecialHour {
  id?: string;
  date: string;
  startTime: string;
  endTime: string;
  shopId?: string | null;
}

export interface TimeOff {
  id: string;
  startTs: string;
  endTs: string;
  reason?: string | null;
  createdAt?: string;
}

export interface ScheduleSettingsResponse {
  success: boolean;
  data: {
    workingHours: WorkingHour[];
    specialHours: SpecialHour[];
    timeOff: TimeOff[];
    range: { start: string; end: string };
  };
  message?: string;
}

export const fetchStaffs = async (params?: GetStaffsParams): Promise<Staff[]> => {
  const { data } = await apiClient.get<StaffsResponse>("/api/staffs", { params });
  if (!data.success) throw new Error(data.message || "取得人員失敗");
  return data.staffs;
};

export const fetchStaff = async (id: string): Promise<Staff> => {
  const { data } = await apiClient.get<StaffResponse>(`/api/staffs/${id}`);
  if (!data.success || !data.staff) throw new Error(data.message || "取得人員失敗");
  return data.staff;
};

export const createStaff = async (payload: CreateStaffPayload): Promise<Staff> => {
  const { data } = await apiClient.post<StaffResponse>("/api/staffs", payload);
  if (!data.success || !data.staff) throw new Error(data.message || "建立人員失敗");
  return data.staff;
};

export const updateStaff = async (id: string, payload: UpdateStaffPayload): Promise<Staff> => {
  const { data } = await apiClient.put<StaffResponse>(`/api/staffs/${id}`, payload);
  if (!data.success || !data.staff) throw new Error(data.message || "更新人員失敗");
  return data.staff;
};

export const reorderStaffs = async (orders: { id: string; display_order: number }[]): Promise<void> => {
  await apiClient.put("/api/staffs/reorder", { orders });
};

export const deleteStaff = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/staffs/${id}`);
};

export const upsertStaffAccount = async (id: string, payload: StaffAccountPayload): Promise<Staff> => {
  const { data } = await apiClient.post<StaffAccountResponse>(`/api/staffs/${id}/account`, payload);
  if (!data.success || !data.staff) throw new Error(data.message || "儲存帳號失敗");
  return data.staff;
};

export const fetchStaffScheduleSettings = async (
  id: string,
  params?: { start_date?: string; end_date?: string }
): Promise<ScheduleSettingsResponse["data"]> => {
  const { data } = await apiClient.get<ScheduleSettingsResponse>(`/api/staffs/${id}/schedule-settings`, {
    params
  });
  if (!data.success) throw new Error(data.message || "取得排班設定失敗");
  return data.data;
};

export const updateStaffWorkingHours = async (
  id: string,
  workingHours: WorkingHour[]
): Promise<WorkingHour[]> => {
  const { data } = await apiClient.put<{ success: boolean; workingHours: WorkingHour[] }>(
    `/api/staffs/${id}/working-hours`,
    { workingHours }
  );
  return data.workingHours;
};

export const updateStaffSpecialHours = async (
  id: string,
  specialHours: SpecialHour[]
): Promise<SpecialHour[]> => {
  const { data } = await apiClient.put<{ success: boolean; specialHours: SpecialHour[] }>(
    `/api/staffs/${id}/special-hours`,
    { specialHours }
  );
  return data.specialHours;
};

export const fetchStaffTimeOff = async (
  id: string,
  params?: { start_date?: string; end_date?: string }
): Promise<TimeOff[]> => {
  const { data } = await apiClient.get<{ success: boolean; timeOff: TimeOff[] }>(
    `/api/staffs/${id}/time-off`,
    { params }
  );
  return data.timeOff;
};

export const createStaffTimeOff = async (
  id: string,
  payload: { start_ts: string; end_ts: string; reason?: string | null }
): Promise<TimeOff> => {
  const { data } = await apiClient.post<{ success: boolean; timeOff: TimeOff }>(
    `/api/staffs/${id}/time-off`,
    payload
  );
  return data.timeOff;
};

export const deleteStaffTimeOff = async (id: string, timeOffId: string): Promise<void> => {
  await apiClient.delete(`/api/staffs/${id}/time-off/${timeOffId}`);
};

// ─── 職位抽成模板 ───

export type PositionTemplates = Record<string, CommissionTier[]>;

export const fetchPositionTemplates = async (): Promise<PositionTemplates> => {
  const { data } = await apiClient.get<{ success: boolean; templates: PositionTemplates }>("/api/staffs/position-templates");
  return data.templates;
};

export const savePositionTemplate = async (positionName: string, tiers: CommissionTier[]): Promise<void> => {
  await apiClient.put(`/api/staffs/position-templates/${encodeURIComponent(positionName)}`, { tiers });
};

// ─── 獎金選項 (Presets) ───

export interface StaffBonusPreset {
  id: string;
  name: string;
  amount: number;
  created_at: string;
}

export const fetchBonusPresets = async (): Promise<StaffBonusPreset[]> => {
  const { data } = await apiClient.get<StaffBonusPreset[]>("/api/staffs/bonus-presets");
  return data;
};

export const createBonusPreset = async (payload: { name: string; amount: number }): Promise<StaffBonusPreset> => {
  const { data } = await apiClient.post<StaffBonusPreset>("/api/staffs/bonus-presets", payload);
  return data;
};

export const updateBonusPreset = async (id: string, payload: { name: string; amount: number }): Promise<StaffBonusPreset> => {
  const { data } = await apiClient.put<StaffBonusPreset>(`/api/staffs/bonus-presets/${id}`, payload);
  return data;
};

export const deleteBonusPreset = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/staffs/bonus-presets/${id}`);
};

// ─── 特殊款項 ───

export interface StaffSpecialBonus {
  id: string;
  staff_id: string;
  name: string;
  amount: number;
  period: string;
  created_at: string;
}

export const fetchStaffSpecialBonuses = async (period: string): Promise<StaffSpecialBonus[]> => {
  const { data } = await apiClient.get<StaffSpecialBonus[]>("/api/staffs/special-bonuses", { params: { period } });
  return data;
};

export const createStaffSpecialBonus = async (payload: { staff_id: string; name: string; amount: number; period: string }): Promise<StaffSpecialBonus> => {
  const { data } = await apiClient.post<StaffSpecialBonus>("/api/staffs/special-bonuses", payload);
  return data;
};

export const deleteStaffSpecialBonus = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/staffs/special-bonuses/${id}`);
};

// ─── 薪資結算（凍結快照，避免歷史數字被回溯性改動）───

export interface SalaryClosing<TRow = unknown> {
  period: string;
  closed_by: string;
  closed_at: string;
  snapshot: TRow[];
}

export const fetchSalaryClosing = async <TRow = unknown>(period: string): Promise<SalaryClosing<TRow> | null> => {
  const { data } = await apiClient.get<{ success: boolean; closing: SalaryClosing<TRow> | null }>(
    "/api/staffs/salary-closings",
    { params: { period } }
  );
  return data.closing;
};

export const createSalaryClosing = async <TRow = unknown>(period: string, snapshot: TRow[]): Promise<SalaryClosing<TRow>> => {
  const { data } = await apiClient.post<{ success: boolean; closing: SalaryClosing<TRow> }>(
    "/api/staffs/salary-closings",
    { period, snapshot }
  );
  return data.closing;
};

export const deleteSalaryClosing = async (period: string): Promise<void> => {
  await apiClient.delete(`/api/staffs/salary-closings/${period}`);
};
